/**
 * ErpStockAuthoritySection — per-location "the ERP is the source of truth for
 * stock" toggle (BLU-715b).
 *
 * WHAT THE SWITCH AUTHORISES
 * --------------------------
 * The reconcile's phantom-zeroing pass acts on an ABSENCE: an ERP-managed SKU
 * with no `sku_inventory` row at an ERP-mapped location gets its Shopify level
 * set to 0. Those writes bypass `pushSingleSku` entirely
 * (`inventorySetQuantities` with a compare quantity), so `last_pushed_qty` is
 * never updated and nothing pushes the real number back. The backend re-reads
 * ERP ownership and authority immediately before that compare-and-set write.
 * Effectively one-way, at up to 10k levels a night. This switch is the
 * per-location allow-list that pass consults, which is why the copy says
 * "zeroed" out loud instead of hiding behind "source of truth".
 *
 * TWO KEYS, BOTH REQUIRED
 * -----------------------
 * Zeroing needs this per-location bit AND the org-wide
 * `inventoryPush.reconcile.zeroUnstockedLocations` flag (pinned to `false` by
 * default in `packages/shared/src/schemas/organization.ts`). Either one off
 * means nothing is zeroed. The org flag has no UI yet, so the note in the
 * intro states the dependency in words rather than cross-linking a control
 * that does not exist. When that flag gets a switch, this note should become a
 * live reference to it.
 *
 * AUTO-CLEAR ON REBIND
 * --------------------
 * `locations_erp_authority_autoclear` (migration 20260731170000) disarms the
 * bit whenever `shopify_location_id` or `delegated_to_location_id` changes,
 * because the bit asserts ERP completeness for ONE point-in-time binding. The
 * intro says so: without that sentence, a switch that silently flips itself
 * off after a rebind reads as a bug and gets re-armed without re-checking.
 *
 * STRUCTURALLY INELIGIBLE LOCATIONS
 * ---------------------------------
 * Two location shapes can never enter the reconcile's `authoritative` set no
 * matter what the bit says: a delegated origin (skipped by a `continue` above
 * the `authoritative.add`) and a location with no `shopify_location_id` (never
 * selected at all). See `isEligibleForZeroing` below for the exact lines. Those
 * rows render a disabled switch and the reason instead of an armable control,
 * because an amber "may be corrected and zeroed" row at a location the pass
 * cannot read is simply a false statement.
 *
 * A row that is ALREADY armed and ineligible (SQL, or armed-then-delegated
 * before the auto-clear trigger) is the one combination that must read as
 * neither: it keeps the armed tint, gains an "Armed, no effect" pill and its
 * own header badge, and its switch stays operable in the OFF direction so the
 * stale bit can actually be cleared from the UI.
 *
 * OWNED AND CLIENT-SHARED TARGETS, ACTIVE ONLY
 * -------------------------------
 * `listErpAuthorityTargets` returns active owned locations plus active shared
 * junctions where this organization is the client. Shared authority is stored
 * on that client junction and never inherited from the floor owner. The active half is
 * why the count badge and the footer both say "active" out loud: a floor armed
 * and then deactivated is absent from this list, so an unqualified "Armed at 3
 * of 5" would be a completeness claim the query cannot back. The error toast
 * still surfaces FORBIDDEN rather than assuming it cannot happen.
 *
 * STRUCTURE
 * ---------
 * Mirrors `features/production-settings/components/PreprintSettingsSection.tsx`
 * (reserved spinner slot so the switch never shifts mid-toggle, flex-wrap row
 * with min-w-0 / shrink-0 clusters, one specific live region, IMS-Settings
 * footer) with three deliberate deviations:
 *
 *  1. ONE switch per row, so the row carries the consequence sentence instead
 *     of a permission summary.
 *  2. An enable-only confirm dialog. Disarming is always safe and instant;
 *     arming the WRONG row in a stacked list is the documented 100k-level
 *     failure mode, so the dangerous direction gets a beat and the safe one
 *     does not.
 *  3. NO success toast. The row restates itself on every toggle (tint, icon,
 *     sentence, header count), which DESIGN.md 15.4 calls the success cue, and
 *     sonner's own live region plus the sr-only one below would otherwise
 *     announce the same sentence twice. Failures still toast, and persist.
 *
 * It is also NOT collapsible, unlike its sibling cards on this page: which
 * floors are armed must not be hideable behind a collapsed header.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  DatabaseZap,
  ExternalLink,
  Loader2,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Unplug,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { NoteLine } from '@/components/shared/NoteLine';
import { StatusPill } from '@/components/shared/StatusPill';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';

/** Stable ids so a repeated failure replaces its predecessor instead of stacking. */
const ERROR_TOAST_ID = 'erp-stock-authority-error';

/**
 * Can the phantom-zeroing pass ever act at this location? This is STRUCTURAL,
 * not a preference — both answers are enforced by `loadShopifyLocationMap`
 * (`services/shopify-inventory-reconcile-cron.ts:216`) before the bit is even
 * read:
 *
 *  - Its SELECT carries `.not('shopify_location_id','is',null)`, so an unmapped
 *    location never enters the loop at all.
 *  - Its `if (row.delegated_to_location_id) { …; continue; }` sits ABOVE the
 *    `authoritative.add(row.id)` line, so a delegated origin is skipped
 *    whatever its stored bit says. Fulfilment routes to the delegate, and the
 *    bit describes a binding this org no longer ships through.
 *
 * So an ineligible location can never enter the `authoritative` set. Offering
 * it an armable switch would promise a consequence the pass cannot deliver, and
 * arming it would leave an amber "may be zeroed" row that is simply untrue.
 */
function isEligibleForZeroing(location: {
  delegatedToLocationId: string | null;
  shopifyLocationId: string | null;
}): boolean {
  return location.delegatedToLocationId === null && location.shopifyLocationId !== null;
}

interface ErpStockAuthoritySectionProps {
  /** When true, renders without the Card wrapper for embedding in a console. */
  embedded?: boolean;
  /** Single-location mode: render only this location's row. */
  locationId?: string;
}

export function ErpStockAuthoritySection({
  embedded = false,
  locationId,
}: ErpStockAuthoritySectionProps) {
  const locationsQuery = trpc.stockSync.listErpAuthorityTargets.useQuery();

  // Arming is confirmed before it is sent; this holds the row awaiting that
  // confirmation. Null while no dialog is open.
  const [pendingArm, setPendingArm] = useState<{
    id: string;
    name: string;
    targetType: 'owned_location' | 'shared_client';
  } | null>(null);

  // A bare "for this location" message is ambiguous in a five-row list, so the
  // live region and the error toast both name the floor they are about.
  const nameOf = (id: string | undefined) =>
    locationsQuery.data?.find((l) => l.id === id)?.name ?? 'this location';

  const setAuthorityMutation = trpc.stockSync.setErpAuthority.useMutation({
    // Awaited on purpose: react-query holds the mutation `pending` until an
    // async onSuccess resolves, so the reserved spinner slot stays filled (and
    // the confirm dialog stays up) until the list already shows the new state.
    // Without the await the switch snaps back to its old position for a frame
    // after a confirmed arm, which reads as "it didn't take".
    //
    // No success toast: the refetched row IS the confirmation (tint, icon,
    // sentence, header count all flip), and sonner announces into its own live
    // region, so a toast here would read the identical sentence to a screen
    // reader twice. DESIGN.md 15.4 — skip the toast when the UI shows the change.
    onSuccess: async () => {
      // refetch() resolves with an error result rather than rejecting, so there
      // is nothing to catch here; a failed refresh just leaves the stale row.
      await locationsQuery.refetch();
    },
    onError: (error, variables) => {
      toast.error(
        `Failed to change PrintRun stock authority at ${nameOf(variables.locationId)}: ${error.message}`,
        // Errors persist until dismissed: this one leaves the operator believing
        // a floor is armed when it is not.
        { id: ERROR_TOAST_ID, duration: Infinity }
      );
    },
    onSettled: () => setPendingArm(null),
  });

  // One live region, and its text names both the direction and the floor, so a
  // second toggle never re-announces an identical sentence.
  const lastChange = setAuthorityMutation.isSuccess ? setAuthorityMutation.variables : null;
  const liveMessage = lastChange
    ? `PrintRun ${lastChange.enabled ? 'is now' : 'is no longer'} the source of truth for stock at ${nameOf(lastChange.locationId)}`
    : '';

  const isLoading = locationsQuery.isLoading;
  const hasError = locationsQuery.isError;
  const allLocations = locationsQuery.data || [];
  const locations = locationId ? allLocations.filter((l) => l.id === locationId) : allLocations;

  // Resolve a delegation target to its name so the row can say WHERE the work
  // went. The target may be inactive or owned by another org, in which case it
  // is absent from this (own-org, active-only) list — the row still reads
  // correctly, just without the name.
  const delegateNameOf = (id: string | null) =>
    (id ? allLocations.find((l) => l.id === id)?.name : null) ?? null;

  const armedCount = locations.filter(
    (l) => l.erpAuthoritativeForStock && isEligibleForZeroing(l)
  ).length;
  const eligibleCount = locations.filter(isEligibleForZeroing).length;
  // Armed rows the pass can never read. Normally zero; non-zero means someone
  // set the bit in SQL, or armed a floor that was later delegated before the
  // auto-clear trigger existed. Surfaced in the header rather than folded into
  // `armedCount`, because a stale bit that vanishes from the count is exactly
  // the thing this card exists to make un-hideable.
  const staleArmedCount = locations.filter(
    (l) => l.erpAuthoritativeForStock && !isEligibleForZeroing(l)
  ).length;

  // The host page is a dirty-model form whose sticky "Unsaved changes / Save
  // changes" bar is in viewport at every scroll position. This card is not part
  // of that model, and a disclaimer buried at the top of a tall body loses that
  // argument to a bar the operator can always see — so the sentence rides with
  // the card's own state readout instead (header beside the armed count, or
  // directly above the switches when this renders headerless).
  const appliesImmediatelyNote = (
    <p className="text-[11px] leading-tight text-muted-foreground">
      Applies immediately. No save step.
    </p>
  );

  const content = (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Turn this on only for a location where PrintRun holds a record for every SKU that
        physically sits there. Once it is on, the reconcile reads a missing PrintRun record as zero
        units and can set that variant&rsquo;s Shopify quantity to 0. Those writes are one way:
        the real number is not pushed back afterwards.
      </p>

      <div className="space-y-2 rounded-md border border-muted bg-muted/30 p-3 text-sm text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">Two keys are required.</span> Zeroing also
          needs the organisation-wide &ldquo;Zero unstocked locations&rdquo; reconcile setting to
          be on. With either key off, no Shopify stock is zeroed.
        </p>
        <p>
          <span className="font-medium text-foreground">This switch clears itself on a rebind.</span>{' '}
          Changing a location&rsquo;s Shopify location or its delegation turns the switch off
          automatically, because the setting is a statement about one specific binding. Re-enable
          it by hand once you have re-checked that PrintRun holds every SKU at the new binding.
        </p>
      </div>

      {/* Error state */}
      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load locations</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {locationsQuery.error?.message || 'Unknown error'}
            <Button
              type="button"
              variant="link"
              className="h-auto p-0"
              onClick={() => locationsQuery.refetch()}
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="ml-2 text-muted-foreground">Loading locations...</span>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !hasError && locations.length === 0 && (
        <div className="flex flex-col items-center rounded-lg bg-muted/30 py-8 text-center">
          <MapPin className="mb-3 h-10 w-10 text-muted-foreground/50" aria-hidden="true" />
          <p className="font-medium">No locations configured</p>
          <p className="mb-4 text-sm text-muted-foreground">
            Add locations in IMS Settings to set stock authority
          </p>
          {/* Router link, not <a href>: a full page load here discards the host
              form's unsaved org settings without giving its "Unsaved changes"
              bar any chance to warn. */}
          <Button variant="outline" asChild className="h-12 gap-2">
            <Link to="/settings?tab=locations">
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              Go to IMS Settings
            </Link>
          </Button>
        </div>
      )}

      {/* Headerless embed has no armed-count cluster to sit beside, so the
          no-save-step signal lands directly above the switches instead. */}
      {embedded && !isLoading && !hasError && locations.length > 0 && appliesImmediatelyNote}

      {/* Location list */}
      {!isLoading && !hasError && locations.length > 0 && (
        <div className="space-y-3" role="list" aria-label="Per-location PrintRun stock authority">
          {locations.map((location) => {
            const armed = location.erpAuthoritativeForStock;
            const rowPending =
              setAuthorityMutation.isPending &&
              setAuthorityMutation.variables?.locationId === location.id &&
              setAuthorityMutation.variables?.targetType === location.targetType;
            const switchId = `erp-stock-authority-${location.id}`;
            const statusId = `erp-stock-authority-status-${location.id}`;
            const eligible = isEligibleForZeroing(location);
            const delegateName = delegateNameOf(location.delegatedToLocationId);
            // A bit that is on where the pass can never read it. Not "armed"
            // and not "off": it is a setting that lies, and the only correct
            // operator action is to clear it.
            const stale = armed && !eligible;
            // One whole literal sentence per state, never assembled from
            // fragments: each states what happens to live Shopify stock, and
            // each stays translatable as a unit. Delegation is checked first
            // because a delegated origin usually still HAS a Shopify id, and
            // "delegated" is the more actionable of the two reasons.
            let summary: string;
            if (location.delegatedToLocationId !== null) {
              summary = armed
                ? `This location is armed, but it is delegated to ${delegateName ?? 'another location'}, so the reconcile acts there instead and can never zero Shopify stock here. Turn the switch off to clear the stale setting.`
                : `Delegated to ${delegateName ?? 'another location'}, so the reconcile acts there instead and can never zero Shopify stock here.`;
            } else if (location.shopifyLocationId === null) {
              summary = armed
                ? 'This location is armed, but it is not mapped to a Shopify location, so there are no Shopify levels for the reconcile to zero here. Turn the switch off to clear the stale setting.'
                : 'Not mapped to a Shopify location, so there are no Shopify levels for the reconcile to zero here.';
            } else {
              summary = armed
                ? 'Shopify stock here may be corrected and zeroed to match PrintRun, including variants PrintRun has no record of.'
                : 'Shopify is never zeroed from missing PrintRun records at this location.';
            }
            // Glyph tracks the three meanings, not the two switch positions:
            // hazard (armed, or armed-where-it-cannot-act), safe (off and
            // eligible), not-wired-in (off and ineligible).
            const StatusIcon = armed ? AlertTriangle : eligible ? ShieldCheck : Unplug;
            return (
              <div
                key={location.id}
                role="listitem"
                className={cn(
                  'overflow-hidden rounded-lg',
                  // Colour is never the only signal: the icon and the sentence
                  // both change with it. The tint exists so "which floors are
                  // armed?" is answerable by scanning one column.
                  armed ? 'bg-amber-50 ring-1 ring-amber-200' : 'bg-muted/30'
                )}
              >
                {/* The switches must stay in one scannable right-hand column —
                    "which floors are armed?" is answered by running an eye down
                    it. So the text takes the slack (`min-w-0 flex-1`, wrapping
                    itself rather than pushing the switch off the line), and the
                    cluster's auto margin holds it right even if it ever does
                    wrap. `justify-between` did neither: a single wrapped item
                    lands at flex-start, dropping the switch to the hard LEFT
                    under its own sentence and inverting the column. */}
                <div
                  className={cn(
                    'flex flex-wrap items-center gap-x-4 gap-y-2 p-3 transition-colors duration-200 motion-reduce:transition-none',
                    armed ? 'hover:bg-amber-100/60' : 'hover:bg-muted/50'
                  )}
                >
                  <div className="min-w-0 flex-1">
                    {/* A stale row must read as neither plain-armed nor
                        plain-ineligible, so it carries a pill the other three
                        states never show (the shared StatusPill, DESIGN.md 7 /
                        13.1). It renders in single-location mode too, where
                        there is no name row to hang off. */}
                    {(!locationId || stale) && (
                      <div className="flex flex-wrap items-center gap-2">
                        {!locationId && <p className="font-medium">{location.name}</p>}
                        {stale && <StatusPill tone="warning">Armed, no effect</StatusPill>}
                      </div>
                    )}
                    {/* The switch's aria-describedby points at `statusId`, which
                        NoteLine puts on the sentence itself. */}
                    <NoteLine
                      id={statusId}
                      icon={StatusIcon}
                      tone={armed ? 'warning' : 'muted'}
                      className="mt-1"
                    >
                      {summary}
                    </NoteLine>
                  </div>

                  <div className="ml-auto flex shrink-0 items-center gap-2">
                    {/* Reserved slot: a spinner that appears from nothing shifts
                        the switch sideways mid-toggle. */}
                    <span className="flex h-3 w-3 shrink-0 items-center justify-center">
                      {rowPending && (
                        <Loader2
                          className="h-3 w-3 animate-spin text-muted-foreground"
                          aria-hidden="true"
                        />
                      )}
                    </span>
                    <Switch
                      id={switchId}
                      checked={armed}
                      onCheckedChange={(next) => {
                        // Belt and braces: the control below is already
                        // unreachable in the ON direction for an ineligible
                        // row, so this can only fire from a stale row heading
                        // OFF. Never open the confirm dialog for a location the
                        // pass cannot read.
                        if (next && !eligible) return;
                        // Arming asks first; disarming is always safe, so it
                        // goes straight through.
                        if (next) {
                          setPendingArm({
                            id: location.id,
                            name: location.name,
                            targetType: location.targetType,
                          });
                        } else {
                          setAuthorityMutation.mutate({
                            targetType: location.targetType,
                            locationId: location.id,
                            enabled: false,
                          });
                        }
                      }}
                      // Ineligible AND off = nothing to do here, so the control
                      // is inert. Ineligible AND on stays OPERABLE on purpose:
                      // disabling a stale bit would trap it in the DB with no
                      // in-app way to clear it, which is worse than the bit
                      // itself. Turning it off is safe at any location.
                      disabled={rowPending || (!eligible && !armed)}
                      // The row has no visible switch label (the location name
                      // is the label), so the accessible name carries both the
                      // capability and the floor, and the consequence sentence
                      // is the description.
                      aria-label={`PrintRun is source of truth for stock at ${location.name}`}
                      aria-describedby={statusId}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Defines both qualifiers the header badge leans on, in one place.
          "Active" is load-bearing, not filler: getLocationSettings filters
          is_active = true, so a floor that was armed and later deactivated is
          absent from this list. "Eligible" is the badge's denominator, and it
          is defined here rather than left as jargon on the badge. */}
      {!isLoading && !hasError && locations.length > 0 && (
        <p className="pt-2 text-xs text-muted-foreground">
          Active Shopify-bound locations are listed here, including shared floors where your
          organisation is the client. Shared authority applies only to your Shopify store and is
          never inherited from the floor owner. To add, remove, or reactivate a location, go to{' '}
          <Link to="/settings?tab=locations" className="underline hover:text-foreground">
            Locations in IMS Settings
          </Link>
          .
        </p>
      )}

      {/* ARIA live region: names the direction and the floor that changed. */}
      <div role="status" aria-live="polite" className="sr-only">
        {liveMessage}
      </div>

      <AlertDialog
        open={pendingArm !== null}
        onOpenChange={(next) => {
          // Ignore overlay clicks and Escape while the write is in flight so
          // the dialog cannot close over an unresolved mutation.
          if (!next && !setAuthorityMutation.isPending) setPendingArm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Make PrintRun the source of truth at {pendingArm?.name ?? 'this location'}?
            </AlertDialogTitle>
            {/* Everything a screen reader gets on open lives in the Description
                (Radix wires only Title + Description into the dialog's
                accessible name/description) — so the two-keys qualifier is
                folded in here rather than left as a visual-only footnote. The
                consequence leads and sits at foreground weight; the
                pre-flight check below is deliberately quieter, because a
                reassurance that out-ranks its own warning is how a confirm
                dialog becomes a rubber stamp. */}
            <AlertDialogDescription className="text-sm text-foreground">
              The reconcile will read a missing PrintRun record at{' '}
              {pendingArm?.name ?? 'this location'} as zero units and can set that
              variant&rsquo;s Shopify quantity to 0, including variants PrintRun has no record of.
              Those levels are not pushed back afterwards.{' '}
              <span className="text-muted-foreground">
                Zeroing also needs the organisation-wide &ldquo;Zero unstocked locations&rdquo;
                reconcile setting to be on.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Not an undo promise. Turning the switch back off stops the next
              run; it restores nothing, because the zeroing writes bypass
              `pushSingleSku` and never update `last_pushed_qty`. */}
          <p className="text-xs text-muted-foreground">
            Only turn this on once you have checked that PrintRun holds every SKU stocked at{' '}
            {pendingArm?.name ?? 'this location'}. Turning it off later stops further zeroing. It
            does not restore levels that have already been set to 0.
          </p>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={setAuthorityMutation.isPending}>Cancel</AlertDialogCancel>
            {/* Orange outline, not destructive red (DESIGN.md 13.6's palette
                for a secondary/destructive action) — because the CONTROL is a
                toggle rather than a deletion, NOT because the consequence is
                reversible. It is not: the zeroing writes bypass
                `pushSingleSku`, never update `last_pushed_qty`, and nothing
                pushes the real number back. Red would misfile a setting as a
                delete; a filled primary would misfile it as the happy path. */}
            <AlertDialogAction
              disabled={setAuthorityMutation.isPending}
              className="border border-orange-300 bg-transparent text-orange-700 hover:bg-orange-50 hover:text-orange-800"
              onClick={(event) => {
                // Keep the dialog open until the write settles; onSettled closes it.
                event.preventDefault();
                if (!pendingArm) return;
                setAuthorityMutation.mutate({
                  targetType: pendingArm.targetType,
                  locationId: pendingArm.id,
                  enabled: true,
                });
              }}
            >
              {setAuthorityMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Turning on...
                </>
              ) : (
                'Turn on'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  if (embedded) return content;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100">
            <DatabaseZap className="h-5 w-5 text-amber-700" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base">PrintRun is source of truth for stock</CardTitle>
            <CardDescription>
              Per location: whether Shopify stock may be corrected and zeroed to match PrintRun.
            </CardDescription>
          </div>
          {/* One right-hand cluster: what the card's state is, and what a
              toggle costs. `ml-auto` keeps it right-aligned on its own line
              when the header wraps (flex-start there would read as a second
              title). "Armed at 3 of 5 eligible" avoids a hand-rolled plural and
              stays readable at every count including 1 of 1; the denominator
              counts only locations the pass can act at, and the footer defines
              both "active" and "eligible" so neither is jargon.

              The second badge only exists in an abnormal state, and it is
              deliberately NOT folded into the first: a stale bit absorbed into
              "armed" would overstate the blast radius, and absorbed into
              "not armed" would hide the one row needing cleanup. */}
          {!isLoading && !hasError && locations.length > 0 && (
            <div className="ml-auto flex shrink-0 flex-col items-end gap-1">
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <Badge
                  variant="outline"
                  className={cn(
                    'tabular-nums',
                    armedCount > 0 ? 'border-amber-300 text-amber-700' : 'text-muted-foreground'
                  )}
                >
                  Armed at {armedCount} of {eligibleCount} eligible
                </Badge>
                {staleArmedCount > 0 && (
                  <Badge
                    variant="outline"
                    className="gap-1 border-amber-300 tabular-nums text-amber-800"
                  >
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    {staleArmedCount} armed with no effect
                  </Badge>
                )}
              </div>
              {appliesImmediatelyNote}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
